import { useEffect } from "react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useDebateCheckout } from "@/hooks/use-debate-pricing";
import { toast } from "sonner";
import { NanaeHeader, NanaeFooter, QSectionHeader } from "@/components/qv3";
import { useAuth } from "@/contexts/AuthContext";
import { socialMeta, pageJsonLd } from "@/lib/seo-meta";


interface PricingSearch {
  checkout?: "cancel" | "success";
}

export const Route = createFileRoute("/pricing")({
  validateSearch: (search: Record<string, unknown>): PricingSearch => ({
    checkout:
      search.checkout === "cancel" || search.checkout === "success"
        ? search.checkout
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Tarifs · Hōjin" },
      {
        name: "description",
        content:
          "3 débats offerts, puis 2,99 € à la carte, 19 €/mois en Solo ou 29 €/mois en Pro (early bird).",
      },
      ...socialMeta({
        title: "Tarifs · Hōjin",
        description: "3 débats offerts, puis 2,99 € à la carte, 19 €/mois en Solo ou 29 €/mois en Pro (early bird).",
        url: "https://hojin.ai/pricing",
        image: "keyframe",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/pricing" }],
    scripts: pageJsonLd({
      path: "/pricing",
      name: "Tarifs · Hōjin",
      description: "3 débats offerts, puis 2,99 € à la carte, 19 €/mois en Solo ou 29 €/mois en Pro (early bird).",
      type: "WebPage",
      image: "keyframe",
      breadcrumb: [{ name: "Tarifs", path: "/pricing" }],
    }),
  }),
  component: PricingPage,
});

function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { checkout: checkoutStatus } = useSearch({ from: "/pricing" });
  const debateCheckout = useDebateCheckout();

  useEffect(() => {
    if (checkoutStatus === "cancel") {
      toast("Paiement annulé — aucun montant débité.", {
        description: "Vous pouvez réessayer à tout moment.",
      });
      navigate({ to: "/pricing", search: {}, replace: true });
    }
  }, [checkoutStatus, navigate]);

  const onBuyDebate = () => {
    debateCheckout.mutate(undefined, {
      onError: () =>
        toast.error("Impossible d'ouvrir le paiement. Veuillez réessayer."),
    });
  };


  return (
    <div className="nanae-page qv3-dark">
      <NanaeHeader
        title="Tarifs"
        user={
          user
            ? {
                email: user.email,
                name:
                  (user.user_metadata as { first_name?: string } | undefined)
                    ?.first_name ?? user.email,
              }
            : undefined
        }
      />

      <main className="nanae-page-main">
        <section style={{ marginBottom: 48 }}>
          <QSectionHeader number="I" title="LES OFFRES" />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              marginTop: 24,
            }}
          >
            {/* 1 — Gratuit */}
            <PricingCard
              title="Gratuit"
              price="0 €"
              tagline="Pour découvrir le Conseil."
              features={["1 délibération offerte", "Les 7 intelligences qui délibèrent", "Verdict + mémo de synthèse"]}
              cta={
                <Link to="/saisine" className="nanae-cta-large">
                  Commencer gratuitement
                </Link>
              }
            />

            {/* 2 — Découverte */}
            <PricingCard
              title="Découverte"
              price="9,99 € / mois"
              tagline="Pour goûter à la délibération régulière."
              features={["5 délibérations / mois", "Les 7 intelligences", "Verdict + mémo de synthèse"]}
              cta={
                <button type="button" disabled aria-disabled="true" className="nanae-cta-large" style={{ cursor: "not-allowed", opacity: 0.6 }}>
                  Bientôt disponible
                </button>
              }
            />

            {/* 3 — Essentiel */}
            <PricingCard
              title="Essentiel"
              price="19,99 € / mois"
              tagline="Le rythme d'un usage régulier."
              features={["12 délibérations / mois", "Les 7 intelligences", "Verdict + mémo de synthèse"]}
              highlight
              topBadge="Recommandé"
              cta={
                <button type="button" disabled aria-disabled="true" className="nanae-cta-large" style={{ cursor: "not-allowed", opacity: 0.6 }}>
                  Bientôt disponible
                </button>
              }
            />

            {/* 4 — Pro */}
            <PricingCard
              title="Pro"
              price="29,99 € / mois"
              tagline="Pour décider vite et souvent."
              features={["20 délibérations / mois", "Les 7 intelligences", "Verdict + mémo de synthèse"]}
              cta={
                <button type="button" disabled aria-disabled="true" className="nanae-cta-large" style={{ cursor: "not-allowed", opacity: 0.6 }}>
                  Bientôt disponible
                </button>
              }
            />

            {/* 5 — À la carte */}
            <PricingCard
              title="À la carte"
              price="2,99 € / délibération"
              subPrice="Sans abonnement"
              tagline="Paie au débat, sans engagement."
              features={["Une délibération à l'unité", "Les 7 intelligences", "Verdict + mémo de synthèse"]}
              cta={
                <button
                  type="button"
                  onClick={onBuyDebate}
                  disabled={debateCheckout.isPending}
                  className="nanae-cta-large"
                  style={{ cursor: debateCheckout.isPending ? "wait" : "pointer" }}
                >
                  {debateCheckout.isPending ? "Redirection…" : "Acheter une délibération — 2,99 €"}
                </button>
              }
            />
          </div>
        </section>
      </main>

      <NanaeFooter variant="minimal" />
    </div>
  );
}

function PricingCard({
  title,
  price,
  subPrice,
  tagline,
  features,
  cta,
  highlight,
  badge,
  topBadge,
}: {
  title: string;
  price: string;
  subPrice?: string;
  tagline: string;
  features: string[];
  cta: React.ReactNode;
  highlight?: boolean;
  badge?: string;
  topBadge?: string;
}) {
  return (
    <article
      style={{
        position: "relative",
        border: highlight
          ? "1px solid #FF1744"
          : "0.5px solid var(--qv3-grey-700, #2a2a2a)",
        padding: "28px 24px",
        background: highlight
          ? "color-mix(in oklab, #FF1744 8%, transparent)"
          : "transparent",
        boxShadow: highlight
          ? "0 0 0 1px #FF1744, 0 20px 40px -20px rgba(255,23,68,0.45)"
          : undefined,
        transform: highlight ? "translateY(-8px)" : undefined,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minHeight: 360,
      }}
    >
      {topBadge && (
        <div
          style={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#FFFFFF",
            color: "#0a0a0a",
            fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            padding: "4px 12px",
            whiteSpace: "nowrap",
          }}
        >
          {topBadge}
        </div>
      )}
      {badge && (
        <div
          style={{
            position: "absolute",
            top: -10,
            left: 16,
            background: "#FF1744",
            color: "#FFFFFF",
            fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            padding: "4px 10px",
          }}
        >
          {badge}
        </div>
      )}

      <h2
        className="qv3-mono-hud is-caps"
        style={{ fontSize: 12, letterSpacing: 2, margin: 0 }}
      >
        {title}
      </h2>

      <div>
        <div
          style={{
            fontFamily: "var(--qv3-font-serif)",
            fontSize: 32,
            lineHeight: 1.1,
          }}
        >
          {price}
        </div>
        {subPrice && (
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 11,
              letterSpacing: "0.08em",
              color: "var(--qv3-grey-200, #f3efe6)",
            }}
          >
            {subPrice}
          </div>
        )}
      </div>

      <p
        style={{
          fontFamily: "var(--qv3-font-serif)",
          fontStyle: "italic",
          color: "var(--qv3-grey-300, #c9c4bb)",
          margin: 0,
        }}
      >
        {tagline}
      </p>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          fontFamily: "var(--qv3-font-serif)",
          fontSize: 14,
          color: "var(--qv3-grey-200, #f3efe6)",
        }}
      >
        {features.map((f) => (
          <li
            key={f}
            style={{
              paddingLeft: 16,
              position: "relative",
            }}
          >
            <span
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                color: "#FF1744",
              }}
            >
              ·
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div>{cta}</div>
    </article>
  );
}
